// admin/src/screens/Books.jsx — the rate-card shelf: every book version (status, immutability
// made plain), a new-draft affordance, and the MARGIN REPORT beside it so a retune is informed
// (the kickoff's rule). The report is money/report.js verbatim — per-main realized multiple +
// the health checks (negatives, stale/stuck holds, unmapped settles, balance drift).

import React, { useEffect, useState } from 'react';
import { navigate } from '@common/router.js';
import { useAdmin } from '../App.jsx';
import { Banner, fmtInt, fmtDateTime } from '../lib/ui.jsx';

export default function Books() {
  const { api } = useAdmin();
  const [books, setBooks] = useState(null);
  const [report, setReport] = useState(null);
  const [reportErr, setReportErr] = useState(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  async function loadBooks() {
    const b = await api.get('admin/books');
    if (b.kind === 'ok') setBooks(b.body.books); else setErr(b.error);
  }
  async function loadReport() {
    const p = new URLSearchParams();
    if (range.from) p.set('from', new Date(range.from).toISOString());
    if (range.to) p.set('to', new Date(range.to).toISOString());
    setReportErr(null);
    const r = await api.get('admin/report' + (p.toString() ? '?' + p.toString() : ''));
    if (r.kind === 'ok') setReport(r.body); // a failed report reads as an error, not a stuck spinner
    else { setReport(null); setReportErr(r.error); }
  }
  useEffect(() => { loadBooks(); loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function draft() {
    setBusy(true); setErr(null);
    const r = await api.post('admin/books/draft', note.trim() ? { note: note.trim() } : {});
    setBusy(false);
    if (r.kind === 'ok') navigate(`/books/${r.body.draft.version}`);
    else setErr(r.error);
  }

  // books come version-DESC, so the first MATCHING active row is the one loadBook() actually
  // quotes from — the LIVE book. Match loadBook exactly: highest active version whose
  // effective_at has arrived (null = immediate). A future-dated activation must NOT show Live
  // until its window opens (money/book.js:16-18 prices `effective_at IS NULL OR <= now`).
  const nowMs = Date.now();
  const liveV = books && books.find((b) => b.status === 'active' && (!b.effective_at || new Date(b.effective_at).getTime() <= nowMs));
  const hasDraft = books && books.some((b) => b.status === 'draft');

  return (
    <div>
      <h1>Rate card</h1>
      {err && <Banner kind="err">{err}</Banner>}
      <div className="card">
        <div className="row">
          <button className="btn primary" disabled={busy} onClick={draft}>New draft (copy of active)</button>
          <input placeholder="draft note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ inlineSize: 320 }} />
          {liveV && <span className="muted">live book: <b>v{liveV.version}</b> · {fmtInt(liveV.credits_per_usd)} credits/USD</span>}
        </div>
        <p className="hint" style={{ margin: '2px 0 0' }}>
          A book is append-only: editing is a new DRAFT version; activating is one-way; an activated version is history forever (a job pinned to it settles under its rows).
          {hasDraft && ' A draft already exists — open it below to keep editing.'}
        </p>
      </div>

      {!books ? <p className="muted">Loading…</p> : (
        <table>
          <thead><tr><th>v</th><th>status</th><th>effective</th><th className="num">credits/USD</th><th className="num">mains</th><th className="num">subs</th><th>note</th></tr></thead>
          <tbody>
            {books.map((b) => (
              <tr key={b.version} className="click" onClick={() => navigate(`/books/${b.version}`)}>
                <td className="mono">{b.version}</td>
                <td><span className={'pill ' + b.status}>{b.status}</span>{liveV && b.version === liveV.version && <span className="chip" style={{ marginInlineStart: 6 }}>live</span>}</td>
                <td className="muted">{b.effective_at ? fmtDateTime(b.effective_at) : '—'}</td>
                <td className="mono num">{fmtInt(b.credits_per_usd)}</td>
                <td className="mono num">{b.mains}</td>
                <td className="mono num">{b.subs ?? '—'}</td>
                <td className="muted">{b.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pagehead" style={{ marginBlockStart: 22 }}>
        <h2 style={{ margin: 0 }}>Margin report <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— the retune-the-menu view</span></h2>
        <div className="row">
          <label className="hint">from <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
          <label className="hint">to <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
          <button className="btn tiny" onClick={loadReport}>Apply</button>
        </div>
      </div>
      {reportErr ? <Banner kind="err">{reportErr}</Banner> : !report ? <p className="muted">Loading…</p> : (
        <>
          <table>
            <thead><tr><th>main</th><th className="num">jobs</th><th className="num">credits spent</th><th className="num">USD actual</th><th className="num">failed jobs</th><th className="num">failed USD</th><th className="num">realized ×</th></tr></thead>
            <tbody>
              {report.per_main.length === 0 && <tr><td colSpan={7} className="muted">No settled spend in this period.</td></tr>}
              {report.per_main.map((m) => (
                <tr key={m.main}>
                  <td>{m.main}</td>
                  <td className="mono num">{m.jobs}</td>
                  <td className="mono num">{fmtInt(m.credits_spent)}</td>
                  <td className="mono num">${Number(m.usd_actual).toFixed(2)}</td>
                  <td className="mono num">{m.failed_jobs || 0}</td>
                  <td className="mono num">{Number(m.failed_usd) ? '$' + Number(m.failed_usd).toFixed(2) : '—'}</td>
                  <td className={'mono num' + (m.realized_multiple != null && m.realized_multiple < 2 ? ' neg' : '')}>
                    {m.realized_multiple != null ? m.realized_multiple.toFixed(2) + '×' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint" style={{ marginBlockStart: 4 }}>
            realized × = credits earned ÷ (provider USD incl. failures). D23 floor is 2× — anything under is flagged red.
            {report.ledger_kinds.length > 0 && ' · ledger: ' + report.ledger_kinds.map((k) => `${k.kind} ${k.rows}`).join(' · ')}
          </p>
          {/* health checks — must all be quiet on a healthy stack */}
          {report.negative_accounts.length > 0 && <Banner kind="warn">{report.negative_accounts.length} negative-balance account(s) — see Users (they refuse new jobs until topped up).</Banner>}
          {report.stale_holds.length > 0 && <Banner kind="err">STALE HOLDS on {report.stale_holds.length} terminal job(s) — run money/repair.js (a terminal job must not sit on a live hold).</Banner>}
          {report.stuck_queued_holds.length > 0 && <Banner kind="warn">{report.stuck_queued_holds.length} queued job(s) holding credits &gt;30min — a task may have been lost at enqueue.</Banner>}
          {report.unmapped_settles > 0 && <Banner kind="err">{report.unmapped_settles} settle(s) carried UNMAPPED atoms — the book has a pricing gap.</Banner>}
          {report.balance_drift.length > 0 && <Banner kind="err">BALANCE DRIFT (cached ≠ Σ ledger) on {report.balance_drift.length} account(s) — the machinery is unhealthy; do not retune until resolved.</Banner>}
          {report.negative_accounts.length === 0 && report.stale_holds.length === 0 && report.unmapped_settles === 0 && report.balance_drift.length === 0 && report.stuck_queued_holds.length === 0 && (
            <Banner kind="ok">All money-health checks clear.</Banner>
          )}
        </>
      )}
    </div>
  );
}
